/**
 * Fine-grained permissions. Roles are only default bundles: every user can
 * receive individual grants or denials on top of their role.
 */
export const PERMISSIONS = {
  'games.play': 'Jugar a los juegos de la biblioteca',
  'games.request': 'Solicitar juegos nuevos',
  'games.hidden.view': 'Ver títulos ocultos o en pruebas',
  'social.use': 'Amigos, chat y actividad',
  'invites.create': 'Crear invitaciones propias (sujeto a cupo)',
  'patches.apply': 'Aplicar parches de hackroms sobre juegos base',
  'admin.access': 'Acceder al panel de administración',
  'games.manage': 'Gestionar catálogo, metadatos y colecciones',
  'games.upload': 'Subir e importar juegos',
  'users.manage': 'Gestionar usuarios, cuotas y sesiones',
  'permissions.manage': 'Asignar roles y permisos',
  'invites.manage': 'Gestionar todas las invitaciones',
  'storage.manage': 'Gestionar proveedores de almacenamiento y migraciones',
  'settings.manage': 'Configuración general y diagnósticos',
} as const;

export type Permission = keyof typeof PERMISSIONS;
export const ALL_PERMISSIONS = Object.keys(PERMISSIONS) as Permission[];

export const ROLES = ['admin', 'trusted', 'standard'] as const;
export type Role = (typeof ROLES)[number];

const BASE: Permission[] = ['games.play', 'games.request', 'social.use'];

export const ROLE_DEFAULTS: Record<Role, Permission[]> = {
  standard: BASE,
  trusted: [...BASE, 'invites.create', 'patches.apply'],
  admin: ALL_PERMISSIONS,
};

/** Default per-role save quota (bytes). Editable from admin settings. */
export const DEFAULT_ROLE_QUOTAS: Record<Role, number> = {
  standard: 100 * 1024 * 1024,
  trusted: 250 * 1024 * 1024,
  admin: 1024 * 1024 * 1024,
};

export function resolvePermissions(
  role: Role,
  grants: readonly string[],
  denies: readonly string[],
): Permission[] {
  const set = new Set<Permission>(ROLE_DEFAULTS[role]);
  for (const g of grants) if (g in PERMISSIONS) set.add(g as Permission);
  for (const d of denies) set.delete(d as Permission);
  return ALL_PERMISSIONS.filter((p) => set.has(p));
}
